import { useState } from 'react'
import { emptyCutoffCell, nearestCutoff, partnerCutoffLabel } from '../lib/cutoffs'
import { shortDate } from '../lib/site-dates'

// 24-grid stroke glyphs in currentColor — the brand book bans emoji as UI. Sized to the type they
// sit beside (1em), inline, non-scaling stroke, aria-hidden (labels carry the meaning).
// What to show where a leg has no ship, because it is not on one. The API says the mode as a field
// now; before that it arrived as a label inside `vessel` and this line printed it raw.
const INLAND_LABEL: Record<string, string> = {
  rail: 'Rail',
  truck: 'Truck',
  barge: 'Barge',
  inland: 'Inland leg',
  water: 'Vessel not named',
  feeder: 'Feeder not named',
  unknown: 'Vessel not named',
}

const glyph = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  style: { display: 'inline-block', verticalAlign: '-0.125em', flexShrink: 0 },
}
// aria-hidden: the glyphs are decorative; the adjacent text label carries the meaning.
const Anchor = () => (
  <svg {...glyph} aria-hidden role="presentation">
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v13M5 12H3m0 0a9 9 0 0 0 18 0m-2 0h2M8 11H16" />
  </svg>
)
const Ship = () => (
  <svg {...glyph} aria-hidden role="presentation">
    <path d="M3 15l1.6-5.3A2 2 0 0 1 6.5 8H12M12 8V4h4l3 4M12 8h7M4 15h16l-1.5 4.5a2 2 0 0 1-1.9 1.3H7.4a2 2 0 0 1-1.9-1.3L4 15Z" />
  </svg>
)
const Pin = () => (
  <svg {...glyph} aria-hidden role="presentation">
    <path d="M12 21s6-5.3 6-10a6 6 0 1 0-12 0c0 4.7 6 10 6 10Z" />
    <circle cx="12" cy="11" r="2.2" />
  </svg>
)
const Lock = () => (
  <svg {...glyph} aria-hidden role="presentation">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
)

// Vessel-first sailing card: one physical vessel+voyage, with the slot-sharing carriers clustered
// inside as "book via" partners, the routing (transit / legs / transshipment / CO2), the vessel
// particulars from our AIS data (the trust layer), and per-carrier lane reliability. Replaces the
// carrier-first list where the same ship appeared as N duplicate rows.

export interface RouteLeg {
  leg: number
  from?: string | null
  to?: string | null
  from_name?: string | null
  to_name?: string | null
  /** The camelCase spelling an older API sends. Web and API deploy separately, and a browser holds a
   *  cached bundle, so for one release both spellings are read or a leg loses its port names. */
  fromName?: string | null
  toName?: string | null
  /** What the leg travels on. An inland hand-over is not a change of vessel. */
  mode?: 'water' | 'feeder' | 'rail' | 'truck' | 'barge' | 'inland' | 'unknown' | null
  vessel?: string | null
  voyage?: string | null
  service?: string | null
  departure?: string | null
  arrival?: string | null
  distanceKm?: number | null
  co2Teu?: number | null
}
/** Why a carrier's cut-off is missing. "The carrier published none" and "we never held this carrier's,
 *  because we rebuilt the row from its slot partner's schedule" are different facts and used to look
 *  identical on screen. Optional: an older /public/schedules/cards does not send it. */
/**
 * Why a cut-off is missing, in one word. `not_held` is the one that matters: the sailing came from
 * another line selling the same ship, so we never had this carrier's deadline — asking them may still
 * get it. The API sends the word and we write the sentence; it used to send both, plus three fields
 * saying where the row came from.
 */
export type CutoffStatus = 'published' | 'not_published' | 'not_held'
export interface Partner {
  carrier_code: string
  carrier_name: string
  service_name: string | null
  voyage_number: string | null
  published_departure: string | null
  published_arrival: string | null
  reliability_pct: number | null
  reliability_obs: number
  reliability_provisional: boolean
  /** THIS carrier's own cut-offs, never a slot partner's. */
  cutoffs?: { cy: string | null; doc: string | null; vgm: string | null }
  cutoff_status?: CutoffStatus
}
export interface VesselParticulars {
  imo: string | null
  teu: number | null
  teu_estimated: number | null
  length_m: number | null
  beam_m: number | null
  gross_tonnage: number | null
  deadweight: number | null
  year_built: number | null
  flag: string | null
  ship_type: string | null
  lat: number | null
  lng: number | null
  speed_knots: number | null
  nav_status: string | null
  position_updated_at: string | null
  last_port_name: string | null
  destination_name: string | null
  eta_ais: string | null
  near_port: string | null
  near_port_km: number | null
  photo_url: string | null
  builder: string | null
  owner: string | null
  operator: string | null
  class_society: string | null
  operating_status: string | null
  home_port: string | null
}
export interface CardCutoffProvenance {
  status: 'agreed' | 'single_carrier' | 'disagree' | 'none'
  carriers: string[]
}
export interface Confidence {
  sources: number
  etd_spread_hours: number | null
  eta_spread_hours: number | null
  level: 'high' | 'medium' | 'low' | 'single_source'
}
/**
 * What the badge says, in the caller's words. `level` is the WORSE of the departure and arrival
 * spreads, so the badge has to name the date that earned the colour. Printing the departure spread
 * alone would have shown "± 0d apart" on ZIM FALCON 21E, whose departures agree to the hour and whose
 * arrivals are 128 hours apart.
 *
 * And it must not claim a comparison we did not make. Where only one partner published an arrival there
 * is no arrival spread, the level rests on the departure alone, and "schedules match" would be a
 * promise about a field nobody checked.
 */
export function confidenceBadge(c: Confidence): { text: string; title: string } {
  const etd = c.etd_spread_hours
  const eta = c.eta_spread_hours
  const compared =
    etd != null && eta != null ? 'departure and arrival' : eta != null ? 'arrival' : 'departure'
  const title = `The carriers sharing this vessel each publish their own dates. This compares their ${compared}. ✓ = they match (cross-confirmed); ± Nd = they disagree by about N days, so treat with more caution.`
  if (c.level === 'high') {
    return {
      text: eta != null && etd != null ? '✓ schedules match' : `✓ ${compared} matches`,
      title,
    }
  }
  const etdWide = (etd ?? 0) > 24
  const etaWide = (eta ?? 0) > 24
  const field = etdWide && etaWide ? '' : etaWide ? ' on arrival' : ' on departure'
  const widest = Math.max(etd ?? 0, eta ?? 0)
  return { text: `± ${Math.round(widest / 24)}d apart${field}`, title }
}
export interface SailingCardData {
  vessel_name: string | null
  vessel_imo: string | null
  voyage_number: string | null
  service_name: string | null
  /** Load-port terminal (港区) from fob berthing history — Shanghai/Ningbo only; null elsewhere. */
  pol_terminal: { code: string; name: string; port: string } | null
  published_departure: string | null
  published_arrival: string | null
  transit_days: number | null
  is_transshipment: boolean | null
  transship_via: string[] | null
  distance_km: number | null
  co2_teu: number | null
  capacity_teu: number | null
  capacity_estimated: boolean
  route_legs: RouteLeg[] | null
  /** Card-level cut-offs. Null wherever no single value belongs to the whole sailing: the carriers on
   *  one vessel each set their own against their own slot allocation, and they often differ. */
  cutoffs: { cy: string | null; doc: string | null; vgm: string | null }
  /** Whose cut-off the card-level value is. Absent on responses from an older API. */
  cutoffs_provenance?: {
    cy: CardCutoffProvenance
    doc: CardCutoffProvenance
    vgm: CardCutoffProvenance
  }
  /** Plain-language gaps in the cut-off data on this sailing. */
  cutoff_caveats?: string[]
  confidence: Confidence
  prediction: {
    predicted_arrival: string
    delay_hours: number
    basis_obs: number
    band_hours: number
    confidence: 'high' | 'medium' | 'low'
  } | null
  ais_eta: {
    ais_eta: string
    remaining_nm: number
    speed_kn: number
    divergence_hours: number | null
    confidence: 'high' | 'medium' | 'low'
  } | null
  partner_count: number
  partners: Partner[]
  vessel: VesselParticulars | null
  // Latest ETD reschedule for this sailing (from sailing_revisions), if any — drives the timeline's
  // "moved" ghost. Nullable, and may be absent from an older API, so guard at the read site.
  last_move?: { old_departure: string; moved_at: string } | null
}

const fmtDate = (s: string | null | undefined) => shortDate(s)
const fmtDateTime = (s: string | null | undefined) =>
  s
    ? new Date(s).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'
const relColor = (pct: number) =>
  pct >= 78 ? 'var(--signal)' : pct >= 70 ? 'var(--warn)' : 'var(--overdue)'

// nearestCutoff and the empty-cell wording live in lib/cutoffs.ts so they can be unit-tested without a
// DOM harness. Re-exported because ComparePage's pinned tray reads the same logic.
export { nearestCutoff }

export function SailingCard({
  card,
  pinned,
  onTogglePin,
  unlocked,
}: {
  card: SailingCardData
  pinned?: boolean
  onTogglePin?: () => void
  unlocked?: boolean
}) {
  const [open, setOpen] = useState(false)
  const best = card.partners.find((p) => p.reliability_pct != null && !p.reliability_provisional)
  const cutoff = nearestCutoff(card.cutoffs)
  // Which cut-off kind the shown value is, so the cell can name whose deadline it is. `cutoff.label`
  // is CY / SI / VGM; the provenance block is keyed cy / doc / vgm.
  const cutoffKey = cutoff?.label === 'SI' ? 'doc' : cutoff?.label === 'VGM' ? 'vgm' : 'cy'
  const cutoffProv = card.cutoffs_provenance?.[cutoffKey]
  const emptyCutoff = cutoff ? null : emptyCutoffCell(card.partners, card.cutoffs_provenance?.cy)
  // Named when exactly one carrier on the vessel publishes it. The others set their own and we do not
  // hold theirs, so presenting it as the sailing's deadline would be a number to miss a ship on.
  const cutoffOwner =
    cutoff && cutoffProv?.status === 'single_carrier' ? (cutoffProv.carriers[0] ?? null) : null
  const cutoffCaveats = card.cutoff_caveats ?? []
  const legs = card.route_legs ?? []
  const v = card.vessel
  // band_hours/confidence are newer fields; an older /public/schedules/cards (staggered web/API deploy)
  // returns predictions without them, so guard rather than render "±NaNd" / "(undefined confidence)".
  const predBand =
    card.prediction && Number.isFinite(card.prediction.band_hours)
      ? card.prediction.band_hours < 24
        ? `${card.prediction.band_hours}h`
        : `${Math.round(card.prediction.band_hours / 24)}d`
      : null

  return (
    <div className="bg-surface-0 border border-[var(--border-strong)] rounded-2xl overflow-hidden">
      {/* header */}
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[var(--surface-2)] flex items-center justify-center shrink-0 text-lg text-[var(--text-secondary)]">
            <Anchor />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-[var(--text-primary)] truncate">
                {card.vessel_name ?? '—'}
              </span>
              {card.vessel_imo && (
                <span className="font-mono text-xs text-[var(--text-muted)]">
                  IMO {card.vessel_imo}
                </span>
              )}
              {card.pol_terminal && (
                <span
                  className="text-[11px] px-1.5 py-0.5 rounded bg-[var(--surface-2)] text-[var(--text-secondary)] whitespace-nowrap"
                  title={`Load-port terminal (from berthing history): ${card.pol_terminal.name}`}
                >
                  <span className="inline-flex items-center gap-1">
                    <Ship /> {card.pol_terminal.name}
                  </span>
                </span>
              )}
            </div>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">
              {[
                card.service_name && `svc ${card.service_name}`,
                card.voyage_number && `voy ${card.voyage_number}`,
                card.capacity_teu &&
                  `${card.capacity_estimated ? '~' : ''}${card.capacity_teu.toLocaleString()} TEU${
                    card.capacity_estimated ? ' est' : ''
                  }`,
                v?.year_built && `built ${v.year_built}`,
              ]
                .filter(Boolean)
                .join(' · ') || ' '}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {onTogglePin && (
            <button
              type="button"
              onClick={onTogglePin}
              aria-pressed={pinned}
              title={pinned ? 'Remove from comparison' : 'Pin to compare side-by-side'}
              className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors print:hidden ${
                pinned
                  ? 'border-signal/60 bg-signal/10 text-signal'
                  : 'border-border text-[var(--text-secondary)] hover:border-signal/50 hover:text-[var(--text-primary)]'
              }`}
            >
              {pinned ? (
                <span className="inline-flex items-center gap-1">
                  <Pin /> Pinned
                </span>
              ) : (
                '+ Compare'
              )}
            </button>
          )}
          {card.partner_count > 1 && (
            <span className="text-[11px] font-medium whitespace-nowrap rounded-md px-2 py-1 bg-signal/10 text-signal">
              slot-shared · {card.partner_count} lines
            </span>
          )}
          {card.confidence.level !== 'single_source' && (
            <span
              className="text-[10px] font-mono"
              style={{
                color:
                  card.confidence.level === 'high'
                    ? 'var(--signal)'
                    : card.confidence.level === 'medium'
                      ? 'var(--warn)'
                      : 'var(--overdue)',
              }}
              title={confidenceBadge(card.confidence).title}
            >
              {confidenceBadge(card.confidence).text}
            </span>
          )}
        </div>
      </div>

      {/* metrics strip */}
      <div className="grid grid-cols-3 border-y border-border divide-x divide-[var(--border)]">
        <div className="px-5 py-3">
          <div className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-muted)]">
            Transit (est.)
          </div>
          <div className="text-xl font-semibold text-[var(--text-primary)] mt-0.5 tabular-nums">
            {card.transit_days ?? '—'}
            <span className="text-xs font-normal text-[var(--text-secondary)]"> days</span>
          </div>
        </div>
        <div className="px-5 py-3">
          <div className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-muted)]">
            Reliability
          </div>
          {best?.reliability_pct != null ? (
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 h-1.5 rounded-full bg-[var(--surface-2)] overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${best.reliability_pct}%`,
                    background: relColor(best.reliability_pct),
                  }}
                />
              </div>
              <span
                className="text-sm font-medium tabular-nums"
                style={{ color: relColor(best.reliability_pct) }}
              >
                {Math.round(best.reliability_pct)}%
              </span>
            </div>
          ) : (
            <div className="text-xs text-[var(--text-muted)] mt-1.5">
              {card.partners.some((p) => p.reliability_pct != null) ? 'early data' : 'no data yet'}
            </div>
          )}
          <div className="text-[10px] text-[var(--text-muted)] mt-0.5">on-time, 90d</div>
        </div>
        <div className="px-5 py-3">
          <div className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-muted)]">
            {cutoff ? `${cutoff.label} cutoff` : 'Cutoff'}
          </div>
          {cutoff ? (
            <>
              <div className="text-sm font-medium text-[var(--text-primary)] mt-1 tabular-nums">
                {fmtDate(cutoff.when)}
              </div>
              <div
                className="text-[10px] mt-0.5"
                style={{ color: cutoff.days <= 3 ? 'var(--warn)' : 'var(--text-muted)' }}
              >
                {cutoff.days >= 0 ? `in ${cutoff.days}d` : 'closed'}
              </div>
              {cutoffOwner && (
                <div
                  className="text-[9px] uppercase tracking-wide text-[var(--text-muted)] mt-0.5"
                  title={`Only ${cutoffOwner} publishes this cut-off on this vessel. The other carriers set their own and we do not hold theirs.`}
                >
                  {cutoffOwner} only
                </div>
              )}
            </>
          ) : (
            <div
              className="text-xs text-[var(--text-muted)] mt-1"
              title={emptyCutoff?.detail ?? undefined}
            >
              {emptyCutoff?.label ?? 'not published'}
            </div>
          )}
        </div>
      </div>

      {/* routing summary */}
      <div className="px-5 py-2.5 flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-[var(--text-secondary)] border-b border-border">
        {card.is_transshipment != null && (
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: card.is_transshipment ? 'var(--warn)' : 'var(--signal)' }}
            />
            {card.is_transshipment
              ? `Transhipment${card.route_legs?.length ? ` · ${card.route_legs.length} legs` : ''}${
                  card.transship_via?.length ? ` via ${card.transship_via.join(', ')}` : ''
                }`
              : 'Direct'}
          </span>
        )}
        {card.distance_km != null && (
          <span className="font-mono tabular-nums">
            {Math.round(card.distance_km).toLocaleString()} km
          </span>
        )}
        {card.co2_teu != null && (
          <span className="font-mono tabular-nums">{card.co2_teu} t CO₂/TEU</span>
        )}
        <span className="ml-auto font-mono text-[var(--text-muted)] tabular-nums">
          {fmtDate(card.published_departure)} → {fmtDate(card.published_arrival)}
        </span>
        {card.prediction && (
          <span
            className="font-mono tabular-nums w-full text-right"
            style={{
              color: card.prediction.delay_hours >= 24 ? 'var(--warn)' : 'var(--text-muted)',
            }}
            title={`Predicted from this carrier's ${card.prediction.basis_obs} observed sailings on this lane${
              predBand
                ? `; ±${predBand} band reflects its schedule churn (${card.prediction.confidence} confidence)`
                : ''
            }`}
          >
            expected ~{fmtDate(card.prediction.predicted_arrival)} (
            {card.prediction.delay_hours >= 0 ? '+' : ''}
            {Math.round(card.prediction.delay_hours / 24)}d vs published
            {predBand ? `, ±${predBand}` : ''})
          </span>
        )}
        {/* AIS-verified live ETA — independent ground truth vs the carrier's promise (premium). */}
        {card.ais_eta &&
          (unlocked ? (
            <span
              className="font-mono tabular-nums w-full text-right"
              style={{
                color:
                  card.ais_eta.divergence_hours != null && card.ais_eta.divergence_hours >= 24
                    ? 'var(--warn)'
                    : 'var(--signal)',
              }}
              title={`Projected from this vessel's live AIS fix: ${card.ais_eta.remaining_nm} nm out at ${card.ais_eta.speed_kn} kn (${card.ais_eta.confidence} confidence). Carrier ETA stays authoritative.`}
            >
              <span className="inline-flex items-center gap-1">
                <Anchor /> AIS-verified ~{fmtDate(card.ais_eta.ais_eta)}
              </span>
              {card.ais_eta.divergence_hours != null &&
                ` (${card.ais_eta.divergence_hours >= 0 ? '+' : ''}${Math.round(
                  card.ais_eta.divergence_hours / 24
                )}d vs carrier ETA)`}
            </span>
          ) : (
            <span
              className="font-mono tabular-nums w-full text-right text-[var(--text-muted)] opacity-70"
              title="Independent AIS-projected arrival for this vessel: a Pro feature."
            >
              <span className="inline-flex items-center gap-1">
                <Anchor /> AIS-verified ETA · <Lock /> Pro
              </span>
            </span>
          ))}
      </div>

      {/* also bookable with — slot partners */}
      <div className="px-5 pt-3 pb-1">
        <div className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-muted)] mb-2">
          Also bookable with
        </div>
        <div className="flex flex-col gap-1.5">
          {card.partners.map((p, i) => (
            <div
              key={p.carrier_code}
              className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ${
                i === 0 ? 'bg-[var(--surface-1)]' : ''
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="font-mono text-[11px] rounded px-1.5 py-0.5 bg-signal/10 text-signal shrink-0">
                  {p.carrier_code}
                </span>
                <span className="text-[var(--text-primary)] truncate">{p.carrier_name}</span>
                <span className="font-mono text-[11px] text-[var(--text-muted)] truncate hidden sm:inline">
                  {[p.service_name, p.voyage_number].filter(Boolean).join(' · ')}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {i === 0 && (
                  <span className="text-[10px] text-signal hidden sm:inline">
                    ● carrier ETA (baseline)
                  </span>
                )}
                {/* This carrier's OWN cut-off, or the reason we have none for it. A forwarder books a
                    slot, not a vessel, so the deadline that counts is the one on their bill of lading. */}
                {(() => {
                  const cut = partnerCutoffLabel(p)
                  if (!cut.label) return null
                  const held = Boolean(
                    p.cutoffs && (p.cutoffs.cy || p.cutoffs.doc || p.cutoffs.vgm)
                  )
                  return (
                    <span
                      className={`text-[10px] tabular-nums ${
                        held ? 'text-[var(--text-secondary)]' : 'text-[var(--text-faint)] italic'
                      }`}
                      title={cut.detail ?? undefined}
                    >
                      {cut.label}
                    </span>
                  )
                })()}
                {p.reliability_pct != null && (
                  <span
                    className="text-[11px] tabular-nums"
                    style={{ color: relColor(p.reliability_pct) }}
                  >
                    {Math.round(p.reliability_pct)}%{p.reliability_provisional ? '*' : ''}
                  </span>
                )}
                <span className="font-mono text-xs text-[var(--text-secondary)] tabular-nums">
                  {fmtDate(p.published_arrival)}
                </span>
              </div>
            </div>
          ))}
        </div>
        {/* What we do NOT know about this sailing's cut-offs, in plain words. Same job as the
            coverage.caveats block on the vessel endpoint: a gap a reader can act on beats a blank. */}
        {cutoffCaveats.length > 0 && (
          <ul className="mt-2.5 flex flex-col gap-1">
            {cutoffCaveats.map((cav) => (
              <li
                key={cav}
                className="text-[11px] leading-relaxed text-[var(--text-muted)] flex gap-1.5"
              >
                <span aria-hidden className="text-[var(--warn)] shrink-0">
                  ·
                </span>
                <span>{cav}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* expand: vessel particulars + rotation */}
      {(v || legs.length > 0) && (
        <div className="px-5 pb-3">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-xs font-mono text-[var(--text-muted)] hover:text-[var(--text-secondary)] py-1.5"
          >
            {open ? '− hide' : '+ vessel details & port rotation'}
          </button>
          {open && (
            <div className="mt-1 flex flex-col gap-4">
              {v && <VesselCard v={v} name={card.vessel_name} />}
              {legs.length > 0 && (
                <div className="rounded-lg bg-[var(--surface-1)] p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-muted)] mb-2">
                    Rotation · {legs.length} {legs.length === 1 ? 'leg' : 'legs'}
                  </div>
                  <div className="flex flex-col gap-2">
                    {legs.map((l) => (
                      <div key={l.leg} className="flex items-center gap-3 text-xs">
                        <span className="font-mono text-[var(--text-muted)] w-4 shrink-0">
                          {l.leg}
                        </span>
                        <span className="text-[var(--text-secondary)] min-w-0 truncate">
                          {(l.from_name ?? l.fromName ?? l.from) || '—'} →{' '}
                          {(l.to_name ?? l.toName ?? l.to) || '—'}
                        </span>
                        <span className="text-[var(--text-primary)] truncate ml-auto">
                          {l.vessel ?? INLAND_LABEL[l.mode ?? 'unknown']}
                        </span>
                        <span className="font-mono text-[var(--text-muted)] tabular-nums shrink-0 hidden sm:inline">
                          {fmtDate(l.departure)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function VesselCard({ v, name }: { v: VesselParticulars; name: string | null }) {
  const capacity = v.teu
    ? v.teu.toLocaleString()
    : v.teu_estimated
      ? `~${v.teu_estimated.toLocaleString()}`
      : null
  const live =
    v.nav_status || v.last_port_name || v.destination_name || v.near_port || v.speed_knots != null
  // Where it's headed = the AIS destination the master is broadcasting. The most useful single line.
  const headed = v.destination_name?.trim() || null
  // Commercial / who-runs-it facts only — the Gross-t / Deadweight naval trivia is dropped (capacity
  // already lives in the stat rail). Show only the populated rows so sparse vessels stay clean.
  const facts = (
    [
      ['Operator', v.operator],
      ['Owner', v.owner],
      ['Builder', v.builder],
      ['Class', v.class_society],
      ['Type', v.ship_type],
      ['Home port', v.home_port],
    ] as Array<[string, string | number | null | undefined]>
  ).filter(([, val]) => val != null && val !== '')

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--border-strong)] bg-[var(--surface-1)]">
      {/* hero photo */}
      <div className="relative aspect-[21/9] w-full bg-gradient-to-br from-[var(--surface-2)] to-[var(--surface-0)]">
        {v.photo_url && (
          <img
            src={v.photo_url}
            alt={name ?? 'vessel'}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden'
            }}
          />
        )}
        {!v.photo_url && (
          <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)] opacity-40">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <title>vessel</title>
              <circle cx="12" cy="5" r="2" />
              <path d="M12 7v13M5 12h14M7 12a5 5 0 0 0 10 0" />
            </svg>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3">
          <div className="min-w-0">
            <div className="truncate font-display text-base font-bold leading-tight text-white drop-shadow sm:text-lg">
              {name ?? 'Vessel'}
            </div>
            <div className="truncate font-mono text-[11px] text-white/70">
              IMO {v.imo ?? '—'}
              {v.flag ? ` · ${v.flag}` : ''}
            </div>
          </div>
          {v.speed_knots != null && (
            <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-black/55 px-2 py-1 font-mono text-[10px] text-[var(--signal)] backdrop-blur-sm">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--signal)]" />
              {v.speed_knots} kn
            </span>
          )}
        </div>
      </div>

      {/* hero stat rail */}
      <div className="grid grid-cols-3 divide-x divide-[var(--border)] border-b border-[var(--border)]">
        <Stat label="Capacity" value={capacity} unit={capacity ? 'TEU' : undefined} />
        <Stat
          label="Size"
          value={v.length_m && v.beam_m ? `${v.length_m}×${v.beam_m}` : null}
          unit={v.length_m && v.beam_m ? 'm' : undefined}
        />
        <Stat label="Built" value={v.year_built} />
      </div>

      {/* populated detail facts */}
      {facts.length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 p-3 text-xs sm:grid-cols-3">
          {facts.map(([label, val]) => (
            <Fact key={label} label={label} value={val} />
          ))}
        </div>
      )}

      {/* live AIS strip — plain language: where it is now, and where the master says it's going */}
      {live && (
        <div className="border-t border-[var(--border)] bg-[var(--surface-0)]/50 px-3 py-2 text-[11px] text-[var(--text-secondary)]">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--signal)]" />
              Live AIS
            </span>
            {v.near_port ? (
              <span>
                <span className="text-[var(--text-muted)]">
                  {v.near_port_km != null && v.near_port_km > 25 ? 'off' : 'near'}
                </span>{' '}
                <span className="text-[var(--text-primary)]">{v.near_port}</span>
              </span>
            ) : v.lat != null && v.lng != null ? (
              <span className="font-mono tabular-nums">
                {v.lat.toFixed(1)}, {v.lng.toFixed(1)}
              </span>
            ) : null}
            {v.nav_status && <span className="text-[var(--text-muted)]">· {v.nav_status}</span>}
            {v.speed_knots != null && (
              <span className="font-mono tabular-nums text-[var(--text-muted)]">
                · {v.speed_knots} kn
              </span>
            )}
            {v.position_updated_at && (
              <span className="ml-auto text-[var(--text-muted)]">
                {fmtDateTime(v.position_updated_at)}
              </span>
            )}
          </div>
          {headed && (
            <div
              className="mt-1 flex items-center gap-1.5"
              title="Next port of call — the destination the vessel's master is broadcasting over AIS"
            >
              <span aria-hidden className="text-[var(--signal)]">
                →
              </span>
              <span className="text-[var(--text-muted)]">heading</span>
              <span className="text-[var(--text-primary)]">{headed}</span>
              {v.eta_ais && (
                <span className="text-[var(--text-muted)]">· ETA {fmtDateTime(v.eta_ais)}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  unit,
}: {
  label: string
  value: string | number | null | undefined
  unit?: string | undefined
}) {
  return (
    <div className="px-3 py-2.5">
      <div className="font-mono text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </div>
      <div className="mt-0.5 font-display text-lg font-bold leading-none tabular-nums text-[var(--text-primary)]">
        {value ?? <span className="text-[var(--text-muted)]">—</span>}
        {value != null && unit && (
          <span className="ml-1 text-[11px] font-normal text-[var(--text-muted)]">{unit}</span>
        )}
      </div>
    </div>
  )
}

function Fact({
  label,
  value,
  mono,
}: {
  label: string
  value: string | number | null | undefined
  mono?: boolean
}) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[var(--text-muted)]">{label}</span>
      <span className={`text-[var(--text-primary)] text-right truncate ${mono ? 'font-mono' : ''}`}>
        {value ?? '—'}
      </span>
    </div>
  )
}
